import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import PatternDrawing from './PatternDrawing';
import { 
  Save, 
  ArrowLeft, 
  CalendarIcon,
  User,
  Smartphone,
  Euro,
  Settings,
  Search,
  Calculator,
  PenTool
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RepairFormEnhanced = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [estimatedDate, setEstimatedDate] = useState();
  const [deviceData, setDeviceData] = useState({ devices: {}, repair_types: {} });
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    // Client Information
    client_name: '',
    client_phone: '',
    client_email: '',
    
    // Device Information
    device_type: '',
    device_brand: '',
    device_model: '',
    repair_type: '',
    problem_description: '',
    pin_code: '',
    unlock_pattern: '',
    accessories: '',
    
    // Pricing
    total_price: 0,
    advance_payment: 0,
    
    // Management
    assigned_technician: '',
    estimated_exit_date: null,
    notes: ''
  });

  const [patternDrawing, setPatternDrawing] = useState(null);
  const [showPatternDialog, setShowPatternDialog] = useState(false);

  useEffect(() => {
    fetchDeviceData();
    if (isEdit) {
      fetchRepair();
    }
  }, [id, isEdit]);

  const fetchDeviceData = async () => {
    try {
      const response = await axios.get(`${API}/devices/list`);
      setDeviceData(response.data);
    } catch (error) {
      console.error('Error fetching device data:', error);
      toast.error('Erreur lors du chargement des données');
    }
  };

  const fetchRepair = async () => {
    try {
      const response = await axios.get(`${API}/repairs/${id}`);
      const repair = response.data;
      
      setFormData({
        client_name: repair.client_name || '',
        client_phone: repair.client_phone || '',
        client_email: repair.client_email || '',
        device_type: repair.device_type || '',
        device_brand: repair.device_brand || '',
        device_model: repair.device_model || '',
        repair_type: repair.repair_type || '',
        problem_description: repair.problem_description || '',
        pin_code: repair.pin_code || '',
        unlock_pattern: repair.unlock_pattern || '',
        accessories: repair.accessories || '',
        total_price: repair.total_price || 0,
        advance_payment: repair.advance_payment || 0,
        assigned_technician: repair.assigned_technician || '',
        estimated_exit_date: repair.estimated_exit_date || null,
        notes: repair.notes || ''
      });

      if (repair.estimated_exit_date) {
        setEstimatedDate(new Date(repair.estimated_exit_date));
      }

      // Load pattern drawing if exists
      if (repair.unlock_pattern) {
        setPatternDrawing(repair.unlock_pattern);
      }
    } catch (error) {
      console.error('Error fetching repair:', error);
      toast.error('Erreur lors du chargement de la réparation');
      navigate('/repairs');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateSelect = (date) => {
    setEstimatedDate(date);
    setFormData(prev => ({
      ...prev,
      estimated_exit_date: date ? date.toISOString() : null
    }));
  };

  // Calculate remaining amount
  const remainingAmount = useMemo(() => {
    return Math.max(0, (formData.total_price || 0) - (formData.advance_payment || 0));
  }, [formData.total_price, formData.advance_payment]);

  // Get available models based on selected brand
  const getAvailableModels = () => {
    if (!formData.device_type || !formData.device_brand || !deviceData.devices[formData.device_type]) {
      return [];
    }
    
    const models = deviceData.devices[formData.device_type][formData.device_brand] || [];
    
    if (searchTerm) {
      return models.filter(model => 
        model.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return models;
  };

  // Get available repair types based on device type
  const getAvailableRepairTypes = () => {
    if (!formData.device_type) return [];
    
    if (formData.device_type === 'smartphones' || formData.device_type === 'tablets') {
      return deviceData.repair_types.smartphones_tablets || [];
    } else if (formData.device_type === 'consoles') {
      return deviceData.repair_types.consoles || [];
    } else if (formData.device_type === 'pc') {
      return deviceData.repair_types.pc || [];
    }
    
    return deviceData.repair_types.smartphones_tablets || [];
  };

  const validateForm = () => {
    const requiredFields = [
      'client_name', 'client_phone', 'device_type', 'device_brand', 'device_model',
      'repair_type', 'problem_description', 'total_price'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]?.toString().trim()) {
        const fieldLabels = {
          client_name: 'Nom du client',
          client_phone: 'Téléphone',
          device_type: 'Type d\'appareil',
          device_brand: 'Marque',
          device_model: 'Modèle',
          repair_type: 'Type de réparation',
          problem_description: 'Description de la panne',
          total_price: 'Prix total'
        };
        toast.error(`Le champ "${fieldLabels[field]}" est obligatoire`);
        return false;
      }
    }
    
    // Validate phone format
    const phoneRegex = /^[+]?[\d\s\-\(\)]+$/;
    if (!phoneRegex.test(formData.client_phone)) {
      toast.error('Format de téléphone invalide');
      return false;
    }

    // Validate email if provided
    if (formData.client_email && !/\S+@\S+\.\S+/.test(formData.client_email)) {
      toast.error('Format d\'email invalide');
      return false;
    }

    // Validate pricing
    if (formData.total_price < 0) {
      toast.error('Le prix total ne peut pas être négatif');
      return false;
    }

    if (formData.advance_payment < 0) {
      toast.error('L\'acompte ne peut pas être négatif');
      return false;
    }

    if (formData.advance_payment > formData.total_price) {
      toast.error('L\'acompte ne peut pas être supérieur au prix total');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Include pattern drawing if available
      if (patternDrawing) {
        submitData.unlock_pattern = patternDrawing;
      }
      
      if (isEdit) {
        await axios.put(`${API}/repairs/${id}`, submitData);
        toast.success('Réparation mise à jour avec succès');
      } else {
        await axios.post(`${API}/repairs`, submitData);
        toast.success('Réparation créée avec succès');
      }
      
      navigate('/repairs');
    } catch (error) {
      console.error('Error saving repair:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const deviceTypes = [
    { value: 'smartphones', label: 'Smartphone' },
    { value: 'tablets', label: 'Tablette' },
    { value: 'consoles', label: 'Console de jeux' },
    { value: 'pc', label: 'PC/Laptop' }
  ];

  const availableModels = getAvailableModels();
  const availableRepairTypes = getAvailableRepairTypes();
  const technicians = ['Alexandre', 'Sophie', 'Marc', 'Julie', 'Thomas'];

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="repair-form-enhanced">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate('/repairs')}
          className="btn-hover-lift"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {isEdit ? 'Modifier la réparation' : 'Nouvelle réparation'}
          </h1>
          <p className="text-slate-600 mt-1">
            Formulaire complet de gestion des réparations
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* 1. Client Information */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>1. Informations Client</span>
              </CardTitle>
              <CardDescription>
                Coordonnées complètes du client
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Nom du client *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  placeholder="Nom complet du client"
                  className="input-field"
                  data-testid="client-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="client_phone">Téléphone *</Label>
                <Input
                  id="client_phone"
                  value={formData.client_phone}
                  onChange={(e) => handleInputChange('client_phone', e.target.value)}
                  placeholder="+32 4 XX XX XX XX"
                  className="input-field"
                  data-testid="client-phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => handleInputChange('client_email', e.target.value)}
                  placeholder="client@example.com"
                  className="input-field"
                  data-testid="client-email-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* 2. Device Information */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Smartphone className="w-5 h-5" />
                <span>2. Appareil</span>
              </CardTitle>
              <CardDescription>
                Informations détaillées de l'appareil à réparer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="device_type">Type d'appareil *</Label>
                  <Select 
                    value={formData.device_type} 
                    onValueChange={(value) => {
                      handleInputChange('device_type', value);
                      handleInputChange('device_brand', '');
                      handleInputChange('device_model', '');
                    }}
                  >
                    <SelectTrigger data-testid="device-type-select">
                      <SelectValue placeholder="Sélectionnez le type" />
                    </SelectTrigger>
                    <SelectContent>
                      {deviceTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="device_brand">Marque *</Label>
                  <Select 
                    value={formData.device_brand} 
                    onValueChange={(value) => {
                      handleInputChange('device_brand', value);
                      handleInputChange('device_model', '');
                      setSearchTerm('');
                    }}
                    disabled={!formData.device_type}
                  >
                    <SelectTrigger data-testid="device-brand-select">
                      <SelectValue placeholder="Sélectionnez la marque" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.device_type && deviceData.devices[formData.device_type] && 
                        Object.keys(deviceData.devices[formData.device_type]).map(brand => (
                          brand && brand.trim() ? (
                            <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                          ) : null
                        )).filter(Boolean)
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="device_model">Modèle *</Label>
                {formData.device_brand ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher un modèle ou saisir manuellement..."
                        value={searchTerm || formData.device_model}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          handleInputChange('device_model', e.target.value);
                        }}
                        className="pl-10 input-field"
                        data-testid="device-model-search"
                      />
                    </div>
                    
                    {availableModels.length > 0 && searchTerm && (
                      <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md bg-white">
                        {availableModels.slice(0, 20).map((model, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-100 last:border-b-0"
                            onClick={() => {
                              handleInputChange('device_model', model);
                              setSearchTerm('');
                            }}
                          >
                            {model}
                          </div>
                        ))}
                        {availableModels.length > 20 && (
                          <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50">
                            +{availableModels.length - 20} autres modèles...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <Input
                    value={formData.device_model}
                    onChange={(e) => handleInputChange('device_model', e.target.value)}
                    placeholder="Sélectionnez d'abord une marque"
                    disabled
                    className="input-field"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="repair_type">Type de réparation *</Label>
                <Select 
                  value={formData.repair_type} 
                  onValueChange={(value) => handleInputChange('repair_type', value)}
                >
                  <SelectTrigger data-testid="repair-type-select">
                    <SelectValue placeholder="Sélectionnez le type de réparation" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRepairTypes.filter(type => type && type.trim()).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="problem_description">Description de la panne *</Label>
                <Textarea
                  id="problem_description"
                  value={formData.problem_description}
                  onChange={(e) => handleInputChange('problem_description', e.target.value)}
                  placeholder="Décrivez précisément le problème rencontré..."
                  className="input-field"
                  rows={3}
                  data-testid="problem-description-textarea"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pin_code">Code PIN / Schéma</Label>
                  <Input
                    id="pin_code"
                    value={formData.pin_code}
                    onChange={(e) => handleInputChange('pin_code', e.target.value)}
                    placeholder="Pour test après réparation"
                    className="input-field"
                    data-testid="pin-code-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unlock_pattern">Schéma de déverrouillage</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="unlock_pattern"
                      value={formData.unlock_pattern}
                      onChange={(e) => handleInputChange('unlock_pattern', e.target.value)}
                      placeholder="Pattern ou code"
                      className="input-field flex-1"
                      data-testid="unlock-pattern-input"
                    />
                    <Dialog open={showPatternDialog} onOpenChange={setShowPatternDialog}>
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="px-3"
                          data-testid="pattern-draw-button"
                        >
                          <PenTool className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Dessiner le schéma</DialogTitle>
                          <DialogDescription>
                            Dessinez le schéma de déverrouillage de l'appareil pour faciliter les tests après réparation.
                          </DialogDescription>
                        </DialogHeader>
                        <PatternDrawing
                          onPatternChange={(pattern) => {
                            setPatternDrawing(pattern);
                            handleInputChange('unlock_pattern', 'Schéma dessiné - voir image');
                          }}
                          initialPattern={patternDrawing}
                        />
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowPatternDialog(false)}
                          >
                            Fermer
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {patternDrawing && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-700 flex items-center">
                        <PenTool className="w-3 h-3 mr-1" />
                        Schéma dessiné et sauvegardé
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accessories">Accessoires remis</Label>
                <Input
                  id="accessories"
                  value={formData.accessories}
                  onChange={(e) => handleInputChange('accessories', e.target.value)}
                  placeholder="Ex: chargeur, coque, carte SIM..."
                  className="input-field"
                  data-testid="accessories-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* 3. Pricing */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Euro className="w-5 h-5" />
                <span>3. Tarification</span>
              </CardTitle>
              <CardDescription>
                Gestion des prix et paiements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total_price">Prix total (€) *</Label>
                <Input
                  id="total_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.total_price}
                  onChange={(e) => handleInputChange('total_price', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="input-field"
                  data-testid="total-price-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="advance_payment">Acompte payé (€)</Label>
                <Input
                  id="advance_payment"
                  type="number"
                  step="0.01"
                  min="0"
                  max={formData.total_price}
                  value={formData.advance_payment}
                  onChange={(e) => handleInputChange('advance_payment', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="input-field"
                  data-testid="advance-payment-input"
                />
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">Reste à payer:</span>
                  <div className="flex items-center space-x-2">
                    <Calculator className="w-4 h-4 text-slate-500" />
                    <span className="text-xl font-bold text-slate-900" data-testid="remaining-amount">
                      {remainingAmount.toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4. Management */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>4. Gestion</span>
              </CardTitle>
              <CardDescription>
                Assignation et planning de la réparation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="assigned_technician">Technicien assigné</Label>
                <Select 
                  value={formData.assigned_technician} 
                  onValueChange={(value) => handleInputChange('assigned_technician', value)}
                >
                  <SelectTrigger data-testid="technician-select">
                    <SelectValue placeholder="Sélectionnez un technicien" />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.filter(tech => tech && tech.trim()).map(tech => (
                      <SelectItem key={tech} value={tech}>{tech}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Date estimée de fin</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="estimated-date-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {estimatedDate ? format(estimatedDate, "d MMMM yyyy", { locale: fr }) : "Sélectionner une date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={estimatedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes internes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Notes pour l'atelier, diagnostic, pièces nécessaires..."
                  className="input-field"
                  rows={4}
                  data-testid="notes-textarea"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/repairs')}
              className="flex-1"
              data-testid="cancel-button"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 btn-hover-lift"
              data-testid="save-button"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="spinner w-4 h-4 mr-2"></div>
                  {isEdit ? 'Mise à jour...' : 'Création...'}
                </div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEdit ? 'Mettre à jour' : 'Créer la réparation'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RepairFormEnhanced;